import { Component, OnDestroy } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { PaginatedAPI, QueriesAPIModel } from 'src/app/models/api.model'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { getQuerySummaryList } from 'src/app/store/selector/query.selector'
import { load_query_summary_action } from 'src/app/store/action/query.action'
import { QUERY_ID, QUERY_NAME, RAISED_ON, QUERY_TASK_CURRENT_STATUS, TASK_CATEGORY } from 'src/app/constants/ui.columns'
import { DocumentService } from '../../../services/common/document.service'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  getTaskCategorySubCategoryMapFromState,
  getUIStatusMappingFromState
} from '../../../store/selector/initialize.selector'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { QUERY } from 'src/app/constants/field-id-constants'

@Component({
  selector: 'app-query-summary',
  templateUrl: './query-summary.component.html',
  styleUrls: ['./query-summary.component.scss']
})
export class QuerySummaryComponent implements OnDestroy {
  querySummaryList$: Observable<PaginatedAPI<QueriesAPIModel>>
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<QueriesAPIModel>
  private uiStatusMapping: Map<string, string>
  private taskCategoryMap: Map<string, string>
  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'title',
    sortOrder: 'ASC'
  }
  searchRequestBody: any = {
    age: undefined
  }
  pageTitle = 'Query Stats Summary'
  dashboardPageLink = `/${GlobalConstants.ROUTING.QUERY}/${GlobalConstants.ROUTING.DASHBOARD}`
  commonSubscription: Subscription
  readonly MODULE_NAME_QUERIES = GlobalConstants.MODULE.QUERIES

  constructor(private appState: Store<AppState>, private documentService: DocumentService) {
    this.querySummaryList$ = this.appState.select(getQuerySummaryList())
    this.summaryOptions = {
      downloadable: false,
      additionalButtons: false,
      contentSelectable: false,
      contentSelector: true,
      filterable: true,
      formattedContent: false,
      pageable: true,
      sortable: true
    }
  }

  ngOnInit(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe((uiStatusMapping) => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState(this.MODULE_NAME_QUERIES))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          this.taskCategoryMap = new Map(Array.from(categorySubCategoryMap?.keys())?.map(category => [category, category]))
        }
      })

    this.summaryData = {
      content$: this.querySummaryList$,
      contentSelector: {
        label: 'View for Days',
        selectedValue: this.searchRequestBody.age,
        selectionOptions: [
          {
            key: '5',
            value: '5 Days'
          },
          {
            key: '10',
            value: '10 Days'
          },
          {
            key: '15',
            value: '15 Days'
          },
          {
            key: '20',
            value: '20 Days'
          },
          {
            key: '25',
            value: '25 Days'
          },
          {
            key: undefined,
            value: 'ALL'
          }
        ],
        onChange: this.onViewForChange
      },
      formattingLegend: [
        {
          formatLabel: 'High Priority',
          formatClassName: 'priority-high'
        },
        {
          formatLabel: 'Medium Priority',
          formatClassName: 'priority-medium'
        },
        {
          formatLabel: 'Low Priority',
          formatClassName: 'priority-low'
        }
      ],
      headers: [QUERY_ID, QUERY_NAME, RAISED_ON, QUERY_TASK_CURRENT_STATUS],
      additionalButtons: [],
      currentSearchParams: this.queryParamValues,
      sortCallback: this.sortCallback,
      checkBoxSelectionIds: [],
      searchCallback: this.searchCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange
    }
    this.addFiltersWithDynamicOptions()

    this.commonSubscription = this.appState.select('common').subscribe((data) => {
      const searchParams = data?.summaryTableData?.get(QUERY)
      if (searchParams) {
        const status = searchParams?.searchRequestBody?.statusList?.[0]
        const parentStatus = this.uiStatusMapping?.[status]
        let searchRequestBody = searchParams?.searchRequestBody
        if (parentStatus) {
          searchRequestBody = { ...searchRequestBody, statusList: parentStatus }
        }
        this.searchRequestBody = { ...this.searchRequestBody, age: searchParams.searchRequestBody?.age }
        const contentSelector = {
          ...this.summaryData.contentSelector,
          selectedValue: searchParams.searchRequestBody?.age
        }
        this.summaryData = { ...this.summaryData, appliedFilters: searchRequestBody, contentSelector }
        this.appState.dispatch(load_query_summary_action(searchParams))
      } else {
        this.loadSummaryData()
      }
    })
  }

  selectPageCallback = (pageNum) => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  onViewForChange = ($event) => {
    if ($event.target.value != 'undefined') {
      const searchRequestBody = { ...this.searchRequestBody, age: $event.target.value }
      this.searchRequestBody = searchRequestBody
    } else {
      const searchRequestBody = { ...this.searchRequestBody, age: undefined }
      this.searchRequestBody = searchRequestBody
    }
    this.loadSummaryData()
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  back() {}

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  private loadSummaryData() {
    let options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(load_query_summary_action(options))
    this.appState.dispatch(SAVE_SUMMARY_TABLE_SEARCH_PARAMS({ owner: QUERY, data: options }))
  }

  searchCallback = (searchParams) => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some((value) => value !== undefined && value !== '')) {
      searchParams = {}
    }
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus
    }
    this.queryParamValues.pageNo = 0
    this.loadSummaryData()
  }

  rowFormattingForDaysElapsed = (record: QueriesAPIModel): string => {
    if (record.daysElapsed > 7) {
      return 'priority-high'
    } else if (record.daysElapsed > 0 && record.daysElapsed <= 7) {
      return 'priority-medium'
    } else {
      return 'priority-low'
    }
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  addFiltersWithDynamicOptions() {
    // Task Category
    TASK_CATEGORY.filter.getOptionsList = () => {
      return this.taskCategoryMap
    }
    this.summaryData?.headers?.splice(1, 0, TASK_CATEGORY)
  }

  ngOnDestroy(): void {
    this?.commonSubscription?.unsubscribe()
  }
}
